/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */
import { Static, Type } from '@sinclair/typebox';

export interface PropertyTranslationDefinition {
  id: string;
  propertyName?: string;
}

// TODO: move to proper package
export type Action = [string, ...any[]];
export type Condition = LeafCondition | AndCondition | OrCondition;
type LeafCondition = [string, ...any[]];
type AndCondition = ['&&', ...Condition[]];
type OrCondition = ['||', ...Condition[]];

const ActionSchema = Type.Unsafe<Action>(Type.Array(Type.Any()));
const ConditionalStatementSchema = Type.Object({
  if: Type.Unsafe<Condition>(Type.Array(Type.Any())),
  unless: Type.Optional(Type.Unsafe<Condition>(Type.Array(Type.Any()))),
  actions: Type.Array(ActionSchema),
});
export type ConditionalStatement = Static<typeof ConditionalStatementSchema>;

const FlowBlockSchema = Type.Object({
  id: Type.String(),
  conditions: Type.Optional(Type.Array(ConditionalStatementSchema)),
  region: Type.Optional(
    Type.Union([
      Type.Literal('header'),
      Type.Literal('footer'),
      Type.Literal('center'),
    ]),
  ),
  fullWidth: Type.Optional(Type.Boolean()),
  padding: Type.Optional(Type.String()),
  margin: Type.Optional(Type.String()),
});

export const AgreementTranslatableSchema = Type.Object({
  name: Type.Optional(Type.String()),
  content: Type.String(),
  documentationType: Type.Optional(
    Type.Union([Type.Literal('url'), Type.Literal('upload')]),
  ),
  documentationUrl: Type.Optional(Type.String()),
  documentationTitle: Type.Optional(Type.String()),
});

export const AgreementDraftDefinitionSchema = Type.Composite([
  AgreementTranslatableSchema,
  Type.Object({
    formatVersion: Type.Number(),
    translations: Type.Record(
      Type.String(),
      Type.Composite([
        AgreementTranslatableSchema,
        Type.Object({
          id: Type.String({ format: 'uuid' }),
        }),
      ]),
    ),
  }),
]);

export const AgreementVersionSchema = Type.Composite([
  AgreementTranslatableSchema,
  Type.Object({
    id: Type.String(),
    agreementId: Type.String(),
    localeId: Type.String(),
    createdAt: Type.Date({ format: 'date-time' }),
    updatedAt: Type.Date({ format: 'date-time' }),
    deletedAt: Type.Optional(Type.Date({ format: 'date-time', default: null })),
  }),
]);
export type AgreementVersion = Static<typeof AgreementVersionSchema>;

export const AgreementSchema = Type.Composite([
  AgreementTranslatableSchema,
  Type.Object({
    id: Type.String(),
    type: Type.Union([
      Type.Literal('legal'),
      Type.Literal('legal-informative'),
      Type.Literal('additional'),
    ]),
    organizationId: Type.Optional(Type.String()),
    localeId: Type.String(),
    active: Type.Boolean(),
    colorStyle: Type.Optional(Type.String()),
    isDraft: Type.Boolean(),
    draftDefinition: Type.Optional(AgreementDraftDefinitionSchema),
    createdAt: Type.Date({ format: 'date-time' }),
    updatedAt: Type.Date({ format: 'date-time' }),
    deletedAt: Type.Optional(Type.Date({ format: 'date-time', default: null })),
    shared: Type.Boolean(),
  }),
]);
export type Agreement = Static<typeof AgreementSchema>;

export const FlowBlockAgreementSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    // kept as legal for backward compatibility
    type: Type.Literal('legal'),
    agreementId: Type.String(),
    hidden: Type.Optional(Type.Boolean()),
    required: Type.Optional(Type.Boolean()),
    colorStyle: Type.Optional(Type.String()),
  }),
]);
export type FlowBlockAgreement = Static<typeof FlowBlockAgreementSchema>;

// const FlowBlockQuestionSkinSchema = Type.Union([
//   Type.Literal('stars'),
//   Type.Literal('numbers'),
//   Type.Literal('emoticons'),
//   Type.Object({
//     type: Type.Literal('stars'),
//     highlighted: Type.Optional(Type.Boolean()),
//   }),
// ]);

export const ChoiceTranslatableSchema = Type.Object({
  title: Type.String(),
});

const ChoiceDataSchema = Type.Composite([
  ChoiceTranslatableSchema,
  Type.Object({
    id: Type.String(),
    sorting: Type.Optional(Type.Number()),
    score: Type.Optional(Type.Number()),
    isOther: Type.Optional(Type.Boolean()),
  }),
]);

export const ChoiceSchema = Type.Composite([
  ChoiceDataSchema,
  Type.Object({
    // internalId: Type.Number(),
    questionId: Type.String({ format: 'uuid' }),
    localeId: Type.String(),
    reference: Type.Optional(Type.String()),
    createdAt: Type.Date({ format: 'date-time' }),
    updatedAt: Type.Date({ format: 'date-time' }),
    deletedAt: Type.Optional(Type.Date({ format: 'date-time', default: null })),
  }),
]);
export type Choice = Static<typeof ChoiceSchema>;

export const ChoiceVersionSchema = Type.Composite([
  ChoiceTranslatableSchema,
  Type.Object({
    id: Type.String({ format: 'uuid' }),
    choiceId: Type.String({ format: 'uuid' }),
    localeId: Type.String(),
    createdAt: Type.Date({ format: 'date-time' }),
    updatedAt: Type.Date({ format: 'date-time' }),
    deletedAt: Type.Optional(Type.Date({ format: 'date-time', default: null })),
  }),
]);
export type ChoiceVersion = Static<typeof ChoiceVersionSchema>;

export const QuestionTranslatableSchema = Type.Object({
  internalName: Type.Optional(Type.String()),
  description: Type.Optional(Type.String()),
  title: Type.Optional(Type.String()),
  label: Type.Optional(Type.String()),
  commentTitle: Type.Optional(Type.String()),
  commentLabel: Type.Optional(Type.String()),
  lowerBoundLabel: Type.Optional(Type.String()),
  upperBoundLabel: Type.Optional(Type.String()),
  placeholder: Type.Optional(Type.String()),
});

const OpenQuestionSkinSchema = Type.Union([
  Type.Literal('SingleLine'),
  Type.Literal('MultiLine'),
]);
const ChoiceQuestionSkinSchema = Type.Union([
  Type.Literal('List'),
  Type.Literal('Dropdown'),
]);
const SentimentQuestionSkinSchema = Type.Union([
  Type.Literal('Binary'),
  Type.Literal('YesNo'),
]);
const ScaleQuestionSkinSchema = Type.Union([
  Type.Literal('Stars'),
  Type.Literal('StarsSingle'),
  Type.Literal('Emoticons'),
  Type.Literal('NumbersGradient'),
  Type.Literal('Numbers'),
  Type.Literal('YesNo'),
  Type.Literal('Slider'),
  Type.Literal('RadioButtons'),
  Type.Literal('Boxes'),
  Type.Literal('BoxesGradient'),
]);
const QuestionSkinSchema = Type.Union([
  OpenQuestionSkinSchema,
  ChoiceQuestionSkinSchema,
  SentimentQuestionSkinSchema,
  ScaleQuestionSkinSchema,
]);

const QuestionMetricSchema = Type.Union([
  Type.Literal('CSAT'),
  Type.Literal('CES'),
  Type.Literal('FCR'),
  Type.Literal('NPS'),
  Type.Literal('CXJ'),
]);

export const QuestionExtraParamsSchema = Type.Object({
  titleSize: Type.Optional(
    Type.Union([Type.Literal('small'), Type.Literal('large')]),
  ),
  limit: Type.Optional(Type.Number()),
  shuffle: Type.Optional(Type.Boolean()),
  minChoiceLimit: Type.Optional(Type.Number()),
  maxChoiceLimit: Type.Optional(Type.Number()),
  reverseButtonOrder: Type.Optional(Type.Boolean()),
});

const QuestionDataMutableSchema = Type.Composite([
  QuestionTranslatableSchema,
  Type.Object({
    skin: Type.Optional(QuestionSkinSchema),
    lowerBoundScore: Type.Optional(Type.Number()),
    upperBoundScore: Type.Optional(Type.Number()),
    commentAllowed: Type.Optional(Type.Boolean()),
    commentRequired: Type.Optional(Type.Boolean()),
    neutralSentimentBound: Type.Optional(Type.Number()),
    positiveSentimentBound: Type.Optional(Type.Number()),
    maxChoiceCount: Type.Optional(Type.Number()),
    colorStyle: Type.Optional(Type.String()),
    required: Type.Optional(Type.Boolean()),
    extraParams: Type.Optional(QuestionExtraParamsSchema),
  }),
]);

const QuestionDataImmutableSchema = Type.Object({
  type: Type.Union([
    Type.Literal('Scale'),
    Type.Literal('Choice'),
    Type.Literal('Text'),
  ]),
  metric: Type.Optional(QuestionMetricSchema),
});

export const QuestionDraftDefinitionSchema = Type.Composite(
  [
    QuestionDataMutableSchema,
    Type.Object({
      formatVersion: Type.Literal(1),
      translations: Type.Optional(
        Type.Record(
          Type.String(),
          Type.Composite(
            [
              QuestionTranslatableSchema,
              Type.Object({
                id: Type.String({ format: 'uuid' }),
              }),
            ],
            { additionalProperties: false },
          ),
        ),
      ),
      choices: Type.Array(
        Type.Composite(
          [
            ChoiceDataSchema,
            Type.Object({
              translations: Type.Optional(
                Type.Record(
                  Type.String(),
                  Type.Composite(
                    [
                      ChoiceTranslatableSchema,
                      Type.Object({
                        id: Type.String({ format: 'uuid' }),
                      }),
                    ],
                    { additionalProperties: false },
                  ),
                ),
              ),
            }),
          ],
          { additionalProperties: false },
        ),
      ),
    }),
  ],
  { additionalProperties: false },
);

export const QuestionSchema = Type.Composite([
  QuestionDataMutableSchema,
  QuestionDataImmutableSchema,
  Type.Object({
    id: Type.String(),
    reference: Type.Optional(Type.String()),
    isDraft: Type.Boolean(),
    shared: Type.Boolean(),
    organizationId: Type.String(),
    localeId: Type.String(),
    draftDefinition: Type.Optional(QuestionDraftDefinitionSchema),
    createdAt: Type.Date({ format: 'date-time' }),
    updatedAt: Type.Date({ format: 'date-time' }),
    deletedAt: Type.Optional(Type.Date({ format: 'date-time', default: null })),
  }),
]);
export type Question = Static<typeof QuestionSchema>;

export const QuestionVersionSchema = Type.Composite([
  QuestionTranslatableSchema,
  Type.Object({
    id: Type.String({ format: 'uuid' }),
    questionId: Type.String({ format: 'uuid' }),
    localeId: Type.String(),
    createdAt: Type.Date({ format: 'date-time' }),
    updatedAt: Type.Date({ format: 'date-time' }),
    deletedAt: Type.Optional(Type.Date({ format: 'date-time', default: null })),
  }),
]);
export type QuestionVersion = Static<typeof QuestionVersionSchema>;

export const FlowBlockQuestionSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    type: Type.Literal('question'),
    questionId: Type.String(),
    otherChoiceId: Type.Optional(Type.String()),
    autoSubmit: Type.Optional(Type.Boolean()),
    hidden: Type.Optional(Type.Boolean()),
    colorStylePositive: Type.Optional(Type.String()),
    colorStyleNegative: Type.Optional(Type.String()),

    // MOVED TO QUESTION
    // skin: FlowBlockQuestionSkinSchema,
    // required: Type.Optional(Type.Boolean()),
    // commentRequired: Type.Optional(Type.Boolean()),
    // commentAllowed: Type.Optional(Type.Boolean()),
    // placeholder: Type.Optional(Type.String()),

    // MOVED TO QUESTION'S EXTRA PARAMS
    // titleSize: Type.Optional(
    //   Type.Union([Type.Literal('small'), Type.Literal('large')])
    // ),
    // limit: Type.Optional(Type.Number()),
    // shuffle: Type.Optional(Type.Boolean()),
    // minChoiceLimit: Type.Optional(Type.Number()),
    // maxChoiceLimit: Type.Optional(Type.Number()),
  }),
]);
export type FlowBlockQuestion = Static<typeof FlowBlockQuestionSchema>;

const FlowBlockPageBreakTranslatableSchema = Type.Object({
  label: Type.Optional(Type.String()),
});
const FlowBlockPageBreakSchema = Type.Composite([
  FlowBlockSchema,
  FlowBlockPageBreakTranslatableSchema,
  Type.Object({
    type: Type.Literal('break'),
    backgroundColor: Type.Optional(Type.String()),
    withBackButton: Type.Optional(Type.Boolean()),
    actions: Type.Optional(Type.Array(ActionSchema)),
    translations: Type.Optional(
      Type.Record(Type.String(), FlowBlockPageBreakTranslatableSchema),
    ),
  }),
]);
export type FlowBlockPageBreak = Static<typeof FlowBlockPageBreakSchema>;

export const FlowBlockButtonTranslatableSchema = Type.Object({
  label: Type.String(),
});
export const FlowBlockButtonSchema = Type.Composite([
  FlowBlockSchema,
  FlowBlockButtonTranslatableSchema,
  Type.Object({
    type: Type.Literal('button'),
    actions: Type.Array(ActionSchema),
    action: Type.Optional(
      Type.Union([
        Type.Literal('flow.close'),
        Type.Literal('flow.restart'),
        Type.Literal('link'),
      ]),
    ),
    buttonStyle: Type.Optional(
      Type.Union([
        Type.Literal('small'),
        Type.Literal('medium'),
        Type.Literal('large'),
        Type.Literal('link'),
      ]),
    ),
    backgroundColor: Type.Optional(Type.String()),
    color: Type.Optional(Type.String()),
    url: Type.Optional(Type.String()),
    translations: Type.Optional(
      Type.Record(Type.String(), FlowBlockButtonTranslatableSchema),
    ),
  }),
]);
export type FlowBlockButton = Static<typeof FlowBlockButtonSchema>;

export const FlowBlockProfileSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    type: Type.Literal('profile'),
    internalName: Type.Optional(Type.String()),
    size: Type.Union([Type.Literal('small'), Type.Literal('large')]),
    level: Type.Number(),
    showName: Type.Optional(Type.Boolean()),
    showDetails: Type.Boolean(),
    showParentDetails: Type.Boolean(),
    hidden: Type.Optional(Type.Boolean()),
  }),
]);
export type FlowBlockProfile = Static<typeof FlowBlockProfileSchema>;

const FlowBlockHeadlineTranslatableSchema = Type.Object({
  text: Type.String(),
});
const FlowBlockHeadlineSchema = Type.Composite([
  FlowBlockSchema,
  FlowBlockHeadlineTranslatableSchema,
  Type.Object({
    type: Type.Literal('headline'),
    size: Type.Optional(
      Type.Union([Type.Literal('small'), Type.Literal('large')]),
    ),
    color: Type.Optional(Type.String()),
    hidden: Type.Optional(Type.Boolean()),
    translations: Type.Optional(
      Type.Record(Type.String(), FlowBlockHeadlineTranslatableSchema),
    ),
  }),
]);
export type FlowBlockHeadline = Static<typeof FlowBlockHeadlineSchema>;

const FlowBlockParagraphTranslatableSchema = Type.Object({
  text: Type.String(),
});
const FlowBlockParagraphSchema = Type.Composite([
  FlowBlockSchema,
  FlowBlockParagraphTranslatableSchema,
  Type.Object({
    type: Type.Literal('paragraph'),
    color: Type.Optional(Type.String()),
    fontSize: Type.Optional(Type.String()),
    hidden: Type.Optional(Type.Boolean()),
    translations: Type.Optional(
      Type.Record(Type.String(), FlowBlockParagraphTranslatableSchema),
    ),
  }),
]);
export type FlowBlockParagraph = Static<typeof FlowBlockParagraphSchema>;

const FlowBlockLinkTranslatableSchema = Type.Object({
  text: Type.String(),
});
const FlowBlockLinkSchema = Type.Composite([
  FlowBlockSchema,
  FlowBlockLinkTranslatableSchema,
  Type.Object({
    type: Type.Literal('link'),
    actions: Type.Array(ActionSchema),
    translations: Type.Optional(
      Type.Record(Type.String(), FlowBlockLinkTranslatableSchema),
    ),
  }),
]);
export type FlowBlockLink = Static<typeof FlowBlockLinkSchema>;

const FlowBlockRowSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    type: Type.Literal('row'),
    children: Type.Array(
      Type.Union([FlowBlockLinkSchema, FlowBlockParagraphSchema]),
    ),
  }),
]);
export type FlowBlockRow = Static<typeof FlowBlockRowSchema>;

export const FlowBlockDividerSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    type: Type.Literal('divider'),
    internalName: Type.Optional(Type.String()),
    size: Type.Number(),
    dividerType: Type.Union([Type.Literal('line'), Type.Literal('space')]),
    hidden: Type.Optional(Type.Boolean()),
  }),
]);
export type FlowBlockDivider = Static<typeof FlowBlockDividerSchema>;

export const FlowBlockImageSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    type: Type.Literal('image'),
    internalName: Type.Optional(Type.String()),
    url: Type.String(),
    size: Type.Optional(Type.Number()),
    cover: Type.Optional(Type.Boolean()),
    fullWidth: Type.Optional(Type.Boolean()),
    hidden: Type.Optional(Type.Boolean()),
  }),
]);
export type FlowBlockImage = Static<typeof FlowBlockImageSchema>;

export const FlowBlockVideoSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    type: Type.Literal('video'),
    internalName: Type.Optional(Type.String()),
    url: Type.String(),
    size: Type.Number(),
    hidden: Type.Optional(Type.Boolean()),
  }),
]);
export type FlowBlockVideo = Static<typeof FlowBlockVideoSchema>;

export const FlowBlockQRTranslatableSchema = Type.Object({
  title: Type.String(),
});

export const FlowBlockQRSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    type: Type.Literal('qr'),
    internalName: Type.Optional(Type.String()),
    title: Type.Optional(Type.String()),
    value: Type.Optional(Type.String()),
    size: Type.Optional(Type.Number()),
    hidden: Type.Optional(Type.Boolean()),
    translations: Type.Optional(
      Type.Record(Type.String(), FlowBlockQRTranslatableSchema),
    ),
  }),
]);
export type FlowBlockQR = Static<typeof FlowBlockQRSchema>;

export const FlowBlockContainerSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    type: Type.Literal('container'),
    hidden: Type.Optional(Type.Boolean()),
  }),
]);
export type FlowBlockContainer = Static<typeof FlowBlockContainerSchema>;

const FlowBlockCustomerContactSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    type: Type.Literal('customer-contact'),
    // TODO: remove this, can be inferred from the flow
    organizationId: Type.String(),
    customerEmail: Type.String(),
    targetId: Type.String(),
    image: Type.String(),
    width: Type.String(),
  }),
]);
export type FlowBlockCustomerContact = Static<
  typeof FlowBlockCustomerContactSchema
>;

const FlowBlockRestartSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    type: Type.Literal('flow-restart'),
    /** @deprecated */
    timeout: Type.Optional(Type.Number()),
    restartTime: Type.Optional(Type.Number()),
    idleTime: Type.Optional(Type.Number()),
    cancellable: Type.Optional(Type.Boolean()),
  }),
]);
export type FlowBlockRestart = Static<typeof FlowBlockRestartSchema>;

const FlowBlockDataStoreSchema = Type.Object({
  id: Type.String(),
  type: Type.Literal('data-store'),
  preferredLayout: Type.Optional(
    Type.Union([
      Type.Literal('legacy'),
      Type.Literal('single-column'),
      Type.Literal('centered'),
    ]),
  ),
  scrollMode: Type.Optional(
    Type.Union([Type.Literal('auto'), Type.Literal('disabled')]),
  ),
  restart: Type.Optional(
    Type.Union([
      Type.Boolean(),
      Type.Object({
        delay: Type.Optional(Type.Number()),
        timeout: Type.Optional(Type.Number()),
      }),
    ]),
  ),
  themeColors: Type.Optional(Type.Record(Type.String(), Type.String())),
  defaultLocaleId: Type.Optional(Type.String()),
  languageVersions: Type.Optional(
    Type.Array(
      Type.Object(
        {
          id: Type.String(),
          localeId: Type.String(),
          privacyPolicyUrl: Type.Optional(Type.String()),
        },
        {
          additionalProperties: false,
        },
      ),
    ),
  ),
});
export type FlowBlockDataStore = Static<typeof FlowBlockDataStoreSchema>;

export const FlowBlockInputTranslatableSchema = Type.Object({
  label: Type.Optional(Type.String()),
  placeholder: Type.Optional(Type.String()),
  defaultValue: Type.Optional(Type.String()),
});
export const FlowBlockInputSchema = Type.Composite([
  FlowBlockSchema,
  FlowBlockInputTranslatableSchema,
  Type.Object({
    type: Type.Literal('input'),
    required: Type.Boolean(),
    inputType: Type.Union([
      Type.Literal('Number'),
      Type.Literal('String'),
      Type.Literal('Email'),
      Type.Literal('Phone'),
      Type.Literal('Date'),
      Type.Literal('Time'),
    ]),
    name: Type.Optional(Type.String()),
    attributeName: Type.Optional(Type.String()),
    limit: Type.Optional(Type.Number()),
    hidden: Type.Optional(Type.Boolean()),
    defaultValueTemplate: Type.Optional(Type.String()),
    translations: Type.Optional(
      Type.Record(Type.String(), FlowBlockInputTranslatableSchema),
    ),
  }),
]);
export type FlowBlockInput = Static<typeof FlowBlockInputSchema>;

const FlowBlockTargetGridSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    type: Type.Literal('target-grid'),
    targetId: Type.Optional(Type.String()),
  }),
]);
export type FlowBlockTargetGrid = Static<typeof FlowBlockTargetGridSchema>;

const FlowBlockNavigationBarTranslatableSchema = Type.Object({
  title: Type.Optional(Type.String()),
});
const FlowBlockNavigationBarSchema = Type.Composite([
  FlowBlockSchema,
  FlowBlockNavigationBarTranslatableSchema,
  Type.Object({
    type: Type.Literal('navigation-bar'),
    withRestartButton: Type.Optional(Type.Boolean()),
    imageUrl: Type.Optional(Type.String()),
    translations: Type.Optional(
      Type.Record(Type.String(), FlowBlockNavigationBarTranslatableSchema),
    ),
  }),
]);
export type FlowBlockNavigationBar = Static<
  typeof FlowBlockNavigationBarSchema
>;

const FlowBlockTosTranslatableSchema = Type.Object({
  privacyPolicyUrl: Type.Optional(Type.Union([Type.String(), Type.Boolean()])),
});
const FlowBlockTosSchema = Type.Composite([
  FlowBlockSchema,
  FlowBlockTosTranslatableSchema,
  Type.Object({
    type: Type.Literal('tos'),
    translations: Type.Optional(
      Type.Record(Type.String(), FlowBlockTosTranslatableSchema),
    ),
  }),
]);
export type FlowBlockTos = Static<typeof FlowBlockTosSchema>;

const FlowBlockWarningTranslatableSchema = Type.Object({
  mainText: Type.Optional(Type.String()),
  text: Type.Optional(Type.String()),
});
const FlowBlockWarningSchema = Type.Composite([
  FlowBlockSchema,
  FlowBlockWarningTranslatableSchema,
  Type.Object({
    type: Type.Literal('warning'),
    translations: Type.Optional(
      Type.Record(Type.String(), FlowBlockWarningTranslatableSchema),
    ),
  }),
]);
export type FlowBlockWarning = Static<typeof FlowBlockWarningSchema>;

const FlowBlockPassiveSchema = Type.Union([
  FlowBlockProfileSchema,
  FlowBlockHeadlineSchema,
  FlowBlockParagraphSchema,
  FlowBlockVideoSchema,
  FlowBlockDividerSchema,
  FlowBlockImageSchema,
  FlowBlockLinkSchema,
  FlowBlockButtonSchema,
  FlowBlockNavigationBarSchema,
  FlowBlockTosSchema,
  FlowBlockWarningSchema,
  FlowBlockQRSchema,
  FlowBlockContainerSchema,
  FlowBlockRestartSchema,
]);
const FlowBlockActiveSchema = Type.Union([
  FlowBlockInputSchema,
  FlowBlockTargetGridSchema,
  FlowBlockQuestionSchema,
  FlowBlockPageBreakSchema,
  FlowBlockAgreementSchema,
]);

const FlowBlockPageSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    type: Type.Literal('page'),
    hidden: Type.Optional(Type.Boolean()),
    backgroundColor: Type.Optional(Type.String()),
    borderColor: Type.Optional(Type.String()),
    children: Type.Array(
      Type.Union([FlowBlockPassiveSchema, FlowBlockActiveSchema]),
    ),
  }),
]);
export type FlowBlockPage = Static<typeof FlowBlockPageSchema>;

const TerminalPageChildrenSchema = Type.Union([
  FlowBlockPassiveSchema,
  FlowBlockCustomerContactSchema,
]);

const FlowBlockTerminalPageSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    type: Type.Literal('terminal-page'),
    backgroundColor: Type.Optional(Type.String()),
    borderColor: Type.Optional(Type.String()),
    children: Type.Array(TerminalPageChildrenSchema),
  }),
]);
export type FlowBlockTerminalPage = Static<typeof FlowBlockTerminalPageSchema>;

const FlowBlockWarningPageSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    type: Type.Literal('warning-page'),
    backgroundColor: Type.Optional(Type.String()),
    borderColor: Type.Optional(Type.String()),
    children: Type.Array(FlowBlockPassiveSchema),
  }),
]);
export type FlowBlockWarningPage = Static<typeof FlowBlockWarningPageSchema>;

export const FlowBlockRootSchema = Type.Composite([
  FlowBlockSchema,
  Type.Object({
    type: Type.Literal('flow'),
    children: Type.Array(
      Type.Union([
        FlowBlockPageSchema,
        FlowBlockTerminalPageSchema,
        FlowBlockWarningPageSchema,
        FlowBlockDataStoreSchema,
      ]),
    ),
    preferredLayout: Type.Optional(
      Type.Union([
        Type.Literal('legacy'),
        Type.Literal('single-column'),
        Type.Literal('centered'),
      ]),
    ),
    restart: Type.Optional(
      Type.Union([
        Type.Boolean(),
        Type.Object({
          delay: Type.Optional(Type.Number()),
          timeout: Type.Optional(Type.Number()),
        }),
      ]),
    ),
  }),
]);
export type FlowBlockRoot = Static<typeof FlowBlockRootSchema>;

const FlowBlockAll = Type.Union([
  FlowBlockRootSchema,
  FlowBlockPageSchema,
  FlowBlockTerminalPageSchema,
  FlowBlockWarningPageSchema,
  FlowBlockDataStoreSchema,
  FlowBlockActiveSchema,
  FlowBlockPassiveSchema,
]);
export type FlowBlock = Static<typeof FlowBlockAll>;

const FlowLanguageVersionTranslatableSchema = Type.Object({
  privacyPolicyUrl: Type.Optional(Type.String()),
});

export const FlowSchema = Type.Composite([
  FlowLanguageVersionTranslatableSchema,
  Type.Object({
    id: Type.String({ format: 'uuid' }),
    name: Type.String(),
    localeId: Type.String({ format: 'uuid' }),
    questionnaireId: Type.String({ format: 'uuid' }),
    campaignId: Type.String({ format: 'uuid' }),
    definition: Type.Optional(FlowBlockRootSchema),
    createdAt: Type.Date({ format: 'date-time' }),
    updatedAt: Type.Date({ format: 'date-time' }),
    deletedAt: Type.Optional(Type.Date({ format: 'date-time', default: null })),
  }),
]);
export type Flow = Static<typeof FlowSchema>;

export const FlowLanguageVersionSchema = Type.Composite([
  FlowLanguageVersionTranslatableSchema,
  Type.Object({
    id: Type.String(),
    flowId: Type.String(),
    localeId: Type.String(),
    createdAt: Type.Date({ format: 'date-time' }),
    updatedAt: Type.Date({ format: 'date-time' }),
    deletedAt: Type.Optional(Type.Date({ format: 'date-time', default: null })),
  }),
]);
export type FlowLanguageVersion = Static<typeof FlowLanguageVersionSchema>;

export const FlowVersionSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  flowId: Type.String({ format: 'uuid' }),
  definition: Type.Optional(FlowBlockRootSchema),
  publishedAt: Type.Optional(Type.Date({ format: 'date-time' })),
  createdAt: Type.Date({ format: 'date-time' }),
  updatedAt: Type.Date({ format: 'date-time' }),
  deletedAt: Type.Optional(Type.Date({ format: 'date-time', default: null })),
});
export type FlowVersion = Static<typeof FlowVersionSchema>;

export const FlowAssociationSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  flowId: Type.String({ format: 'uuid' }),
  versionId: Type.String({ format: 'uuid' }),
  questionId: Type.Optional(Type.String({ format: 'uuid' })),
  agreementId: Type.Optional(Type.String({ format: 'uuid' })),
  createdAt: Type.Date({ format: 'date-time' }),
  updatedAt: Type.Date({ format: 'date-time' }),
  deletedAt: Type.Optional(Type.Date({ format: 'date-time', default: null })),
});
export type FlowAssociation = Static<typeof FlowAssociationSchema>;

export type FlowEntity =
  | (Agreement & { entityType: 'agreement' })
  | (AgreementVersion & { entityType: 'agreement-version' })
  | (Question & { entityType: 'question' })
  | (QuestionVersion & { entityType: 'question-version' })
  | (Choice & { entityType: 'choice' })
  | (ChoiceVersion & { entityType: 'choice-version' })
  | (FlowLanguageVersion & { entityType: 'flow-language-version' });

export interface FlowContext {
  model: Flow;
  organizationId: string;
  targetId: string;
  interactionId?: string;
  customerId?: string;
  source?: string;
  medium?: string;
  reason?: string;
  localeId: string;
  defaultLocaleId: string;
  parameters?: Record<string, unknown>;
}

export interface FlowDeletedElement {
  id: string;
  flowId: string;
  blockId: string;
  questionId?: string;
  agreementId?: string;
  definition: any;
  createdAt: Date;
  updatedAt: Date;
}

export interface Throttling {
  objectType: 'throttling';
  perInteractionCount?: number;
  perInteractionLimit?: number;
  perCustomerCount?: number;
  perCustomerLimit?: number;
  perTargetCount?: number;
  perTargetLimit?: number;
  requestExpiresAt?: Date;
}

// export interface DraftQuestionData {
//   organizationId?: string;
//   question?: Partial<Question>;
//   translations?: { string; TranslationData };
//   choices?: Array<Partial<Choice>>;
// }

// export interface DraftAgreementData {
//   organizationId?: string;
//   agreement?: Partial<Agreement>;
//   translations?: { string; TranslationData };
// }
